import {
  ShapeProps,
  Slide,
  TextProps,
  chartOptionsToBase64Svg,
} from '@features/powerpoint';
import { contentSlideHeader, textStyles } from '../../../../../utils';
import { Lci3SalesMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { formatters } from '@utils/formattersV2';
import { getSalesLiftChartSVG } from './charts';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';

type Metric = 'inStore' | 'online' | 'total';

type SummaryTableSlideProps = {
  slideMaster: string;
  overallSalesMetrics: Lci3SalesMetrics;
  iroas: number;
};

const fieldHeaderOptions: TextProps['options'] = {
  fontSize: 12,
  bold: true,
};

const fieldValueOptions: TextProps['options'] = {
  ...textStyles.contentSlideDefaults,
  h: 0.2,
  w: 3.26,
  fontSize: 20,
  bold: true,
};

export const salesLiftSummaryChartSlide = (
  props: SummaryTableSlideProps
): Slide => {
  const salesLiftChartSVG = chartOptionsToBase64Svg(
    getSalesLiftChartSVG(props.overallSalesMetrics, 400, 400)
  );

  const createLegendItem = (
    title: string,
    salesMetricKey: Metric,
    options: TextProps['options']
  ): TextProps => {
    return {
      text: [
        {
          text: title,
          options: {
            ...fieldHeaderOptions,
            breakLine: true,
          },
        },
        {
          text: formatters.lift(
            props.overallSalesMetrics.incrementalSalesLift[salesMetricKey],
            {
              decimals: 0,
            }
          ),
          options: {
            ...fieldValueOptions,
            breakLine: true,
          },
        },
        {
          text: `Confidence Level: ${formatters.confidenceLevel(
            props.overallSalesMetrics.confidenceLevel[salesMetricKey],
            props.overallSalesMetrics.incrementalSalesLift[salesMetricKey]
          )}`,
          options: {
            breakLine: true,
            fontSize: 10,
          },
        },
      ],
      options,
    };
  };

  const overallText = createLegendItem('Overall', 'total', {
    lineSpacingMultiple: 1.2,
    valign: 'top',
    h: 1.02,
    w: 2.63,
    x: 0.94,
    y: 2.2,
  });

  const inStoreText = createLegendItem('In-Store', 'inStore', {
    lineSpacingMultiple: 1.2,
    valign: 'top',
    h: 1.02,
    w: 2.63,
    x: 9.9,
    y: 2.2,
  });

  const eCommerceText = createLegendItem('E-Commerce', 'online', {
    lineSpacingMultiple: 1.2,
    valign: 'top',
    h: 1.02,
    w: 2.63,
    x: 9.9,
    y: 3.36,
  });

  const incrementalSalesHeaderText: TextProps = {
    text: 'Incremental Sales',
    options: {
      fontSize: 16,
      bold: true,
      align: 'center',
      h: 0.38,
      w: 2.33,
      x: 4.63,
      y: 1.63,
    },
  };

  const iroasText: TextProps = {
    text: [
      {
        text: 'IROAS',
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: ' (Incremental Return on Ad Spend)',
        options: { fontSize: 9, breakLine: true },
      },
      {
        text: formatters.currency(props.iroas, { decimals: 2 }),
        options: {
          ...fieldValueOptions,
        },
      },
    ],
    options: {
      x: 0.94,
      h: 1.02,
      w: 4,
      y: 3.36,
      lineSpacingMultiple: 1.2,
      valign: 'top',
    },
  };

  const shapes: ShapeProps[] = [
    {
      shapeName: 'rect',
      options: {
        fill: {
          color: unifiedPlatformColorPalette.chartLightBlue,
        },
        h: 0.29,
        w: 0.29,
        x: 0.58,
        y: 2.56,
      },
    },
    {
      shapeName: 'rect',
      options: {
        fill: {
          color: unifiedPlatformColorPalette.chartLightGreen,
        },
        h: 0.29,
        w: 0.29,
        x: 9.54,
        y: 2.56,
      },
    },
    {
      shapeName: 'rect',
      options: {
        fill: {
          color: unifiedPlatformColorPalette.chartDarkBlue,
        },
        h: 0.29,
        w: 0.29,
        x: 9.54,
        y: 3.72,
      },
    },
  ];

  const images = [
    {
      data: salesLiftChartSVG,
      h: 4.96,
      w: 4.96,
      x: 4.22,
      y: 1.82,
    },
  ];

  return {
    slideMaster: props.slideMaster,
    texts: [
      contentSlideHeader('Sales Lift Summary'),
      {
        text: 'Incremental Sales Lift',
        options: {
          fontSize: 16,
          bold: true,
          x: 0.94,
          y: 1.85,
          w: 3.16,
          h: 0.47,
        },
      },
      incrementalSalesHeaderText,
      overallText,
      inStoreText,
      eCommerceText,
      iroasText,
    ],
    shapes,
    images,
  };
};
